import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { QuizModes } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { ComponentsModule } from 'src/app/components/components.module';
import { AlertService } from 'src/app/services/alert.service';
import { CmsServerService } from 'src/app/services/jsonrpc/cms-server.service';
import { GameRecord, QuizTypeServerService } from 'src/app/services/jsonrpc/quiz-type-server.service';
import { Profile, UserServerService } from 'src/app/services/jsonrpc/user-server.service';
import { environment } from 'src/environments/environment';

interface ReviewQuiz {
  uuid: string;
  enabled: boolean;
  meta: any;
  path: string;
  displayName: string;
  breadcrumb: string[];
}

type Totals = {
  attempts: number;
  passed: number;
  failed: number;
  score: number;
  averageScore: number;
  totalTime: number;
  averageTime: number;
};

type DisplayData = {
  [userId: string]: {
    username: string;
    games: GameRecord[];
    totals: Totals;
  };
};

@Component({
  selector: 'review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule, FontAwesomeModule, ComponentsModule]
})
export class ReviewComponent implements OnInit {
  public reviewMaxAttempts = 3;
  public newReviewMaxAttempts = 3;

  public reviewQuizzes: ReviewQuiz[] = [];
  public filteredQuizzes: ReviewQuiz[] = [];
  public searchTerm: string = '';
  public isLoadingQuizzes = false;

  public selectedQuiz: ReviewQuiz | null = null;
  public scores: GameRecord[] = [];
  public users: Profile[] = [];
  public data: DisplayData = {};
  public isLoadingScores = false;
  private expandedUsers: Set<string> = new Set();

  public dateFrom: string = '';
  public dateTo: string = '';
  public today: string = '';

  public Object = Object;

  public isAdmin = false;
  public currentUserUuid: string = '';

  public notCompletedSearch: string = '';
  public notCompletedPage: number = 1;
  public readonly notCompletedPageSize: number = 10;

  public get name() {
    return environment.name;
  }

  public get shortName() {
    return environment.short_name;
  }

  constructor(
    private server: CmsServerService,
    private quizTypeServer: QuizTypeServerService,
    private userServer: UserServerService,
    private alert: AlertService,
    private toastr: ToastrService
  ) {}

  public ngOnInit() {
    this.initializeDateRange();
    this.loadSettings();
    this.initializeUser();
    this.loadReviewQuizzes();
  }

  private async initializeUser() {
    try {
      const profile = await this.userServer.profileGet();
      this.currentUserUuid = profile.uuid;
      this.isAdmin = profile.meta?.roles.includes('admin') || false;

      if (this.isAdmin) {
        await this.fetchUsers();
      } else {
        this.users = [profile];
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.alert.fireError('Failed to load profile', 'Could not load user information');
    }
  }

  private initializeDateRange(): void {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    this.today = this.formatDateForInput(now);
    this.dateTo = this.today;
    this.dateFrom = this.formatDateForInput(thirtyDaysAgo);
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private async loadSettings() {
    try {
      const settings = await this.server.getGameTypeSettings('review');
      this.reviewMaxAttempts = settings.attempts;
      this.newReviewMaxAttempts = settings.attempts;
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }

  private async loadReviewQuizzes() {
    try {
      this.isLoadingQuizzes = true;
      const games = await this.quizTypeServer.getGamesByType(QuizModes.Review);

      this.reviewQuizzes = games.map(game => ({
        ...game,
        displayName: this.extractDisplayName(game.path),
        breadcrumb: this.parseBreadcrumb(game.path)
      }));

      this.filteredQuizzes = [...this.reviewQuizzes];
    } catch (error: any) {
      this.alert.fireError('Failed to load quizzes', error.message || 'An error occurred');
      console.error('Error loading quizzes:', error);
    } finally {
      this.isLoadingQuizzes = false;
    }
  }

  private parseBreadcrumb(path: string): string[] {
    const decodedPath = decodeURIComponent(path);
    const pathWithoutQuery = decodedPath.split('?')[0];
    return pathWithoutQuery.split('/').filter(part => part.trim() !== '');
  }

  private extractDisplayName(path: string): string {
    const breadcrumb = this.parseBreadcrumb(path);
    return breadcrumb[breadcrumb.length - 1] || 'Unnamed Quiz';
  }

  public filterQuizzes(): void {
    if (!this.searchTerm.trim()) {
      this.filteredQuizzes = [...this.reviewQuizzes];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase();
    this.filteredQuizzes = this.reviewQuizzes.filter(
      quiz => quiz.displayName.toLowerCase().includes(searchLower) || quiz.path.toLowerCase().includes(searchLower)
    );
  }

  public async selectQuiz(quiz: ReviewQuiz): Promise<void> {
    this.selectedQuiz = quiz;
    this.expandedUsers.clear();
    await this.loadScores();
  }

  public clearSelection(): void {
    this.selectedQuiz = null;
    this.data = {};
    this.scores = [];
    this.expandedUsers.clear();
  }

  private async fetchScores(): Promise<GameRecord[]> {
    if (!this.selectedQuiz) throw new Error('No quiz selected');

    const fromDate = new Date(this.dateFrom + 'T00:00:00').toISOString();
    const toDate = new Date(this.dateTo + 'T23:59:59').toISOString();

    const allScores = await this.quizTypeServer.getGameTypeSettings(QuizModes.Review, {
      uuid: this.selectedQuiz.uuid,
      dateFrom: fromDate,
      dateTo: toDate
    });

    // Filter scores based on role
    if (this.isAdmin) {
      return allScores;
    } else {
      // Non-admin users can only see their own scores
      return allScores.filter(score => score.user_uuid === this.currentUserUuid);
    }
  }

  private async fetchUsers(): Promise<void> {
    this.users = await this.userServer.userList();
  }

  private calculateDisplayData(scores: GameRecord[], users: Profile[]): DisplayData {
    const userMap = new Map(users.map(u => [u.uuid, u]));
    const data: DisplayData = {};

    for (const score of scores) {
      const user = userMap.get(score.user_uuid);
      if (!data[score.user_uuid]) {
        data[score.user_uuid] = {
          username: user?.username || 'Unknown',
          games: [],
          totals: {
            attempts: 0,
            passed: 0,
            failed: 0,
            score: 0,
            averageScore: 0,
            totalTime: 0,
            averageTime: 0
          }
        };
      }

      const totals = data[score.user_uuid].totals;
      data[score.user_uuid].games.push(score);
      totals.attempts++;
      totals.passed += score.passed ? 1 : 0;
      totals.failed += score.passed ? 0 : 1;
      totals.score += score.score;
      totals.totalTime += score.total_time;
      totals.averageScore = totals.score / totals.attempts;
      totals.averageTime = totals.totalTime / totals.attempts;
    }

    Object.keys(data).forEach(userId => {
      data[userId].games.sort((a, b) => new Date(b.game_started_at).getTime() - new Date(a.game_started_at).getTime());
    });

    return data;
  }

  async loadScores() {
    if (!this.selectedQuiz) {
      this.alert.fireError('No Quiz Selected', 'Please select a quiz first.');
      return;
    }

    if (!this.validateDateRange()) {
      return;
    }

    try {
      this.isLoadingScores = true;
      this.data = {};
      this.expandedUsers.clear();

      // Only fetch scores - users were already loaded at initialization
      const scores = await this.fetchScores();
      this.scores = scores;
      this.data = this.calculateDisplayData(this.scores, this.users);
    } catch (err: any) {
      this.alert.fireError('Failed to load data', `An error occurred: ${err.message || err}`);
      console.error('Error loading quiz scores:', err);
    } finally {
      this.isLoadingScores = false;
    }
  }

  public async searchWithDateRange(): Promise<void> {
    if (!this.validateDateRange()) {
      return;
    }
    await this.loadScores();
  }

  private validateDateRange(): boolean {
    if (!this.dateFrom || !this.dateTo) {
      this.alert.fireError('Invalid Date Range', 'Please select both from and to dates.');
      return false;
    }

    const fromDate = new Date(this.dateFrom);
    const toDate = new Date(this.dateTo);
    const today = new Date();

    if (fromDate > toDate) {
      this.alert.fireError('Invalid Date Range', 'From date cannot be later than to date.');
      return false;
    }

    if (fromDate > today) {
      this.alert.fireError('Invalid Date Range', 'From date cannot be in the future.');
      return false;
    }

    if (toDate > today) {
      this.alert.fireError('Invalid Date Range', 'To date cannot be in the future.');
      return false;
    }

    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 730) {
      this.alert.fireError('Invalid Date Range', 'Date range cannot exceed 2 years.');
      return false;
    }

    return true;
  }

  public onDateRangeChange(): void {
    // Just update the UI, don't automatically search
  }

  toggleUserExpansion(userId: string): void {
    if (this.expandedUsers.has(userId)) {
      this.expandedUsers.delete(userId);
    } else {
      this.expandedUsers.add(userId);
    }
  }

  isUserExpanded(userId: string): boolean {
    return this.expandedUsers.has(userId);
  }

  formatTime(seconds: number): string {
    if (!seconds || seconds === 0) return '0s';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  getAccuracyPercentage(game: GameRecord): number {
    if (game.total_questions === 0) return 0;
    return (game.correct_answers / game.total_questions) * 100;
  }

  formatDateTo24Hour(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  getTotalUsers(): number {
    return Object.keys(this.data).length;
  }

  getTotalAttempts(): number {
    return Object.values(this.data).reduce((sum, user) => sum + user.totals.attempts, 0);
  }

  getTotalPassed(): number {
    return Object.values(this.data).reduce((sum, user) => sum + user.totals.passed, 0);
  }

  getOverallAverageScore(): number {
    const users = Object.values(this.data);
    if (users.length === 0) return 0;

    const totalScore = users.reduce((sum, user) => sum + user.totals.score, 0);
    const totalAttempts = users.reduce((sum, user) => sum + user.totals.attempts, 0);

    return totalAttempts > 0 ? totalScore / totalAttempts : 0;
  }

  public get usersNotCompleted(): Profile[] {
    if (!this.selectedQuiz || this.isLoadingScores) {
      return [];
    }
    const completedUuids = new Set(Object.keys(this.data));
    return this.users.filter(user => user.uuid && !completedUuids.has(user.uuid));
  }

  public get usersNotCompletedFiltered(): Profile[] {
    const term = this.notCompletedSearch.trim().toLowerCase();
    if (!term) return this.usersNotCompleted;
    return this.usersNotCompleted.filter(
      user => user.username?.toLowerCase().includes(term) || user.meta?.mail?.toLowerCase().includes(term)
    );
  }

  public get usersNotCompletedPaged(): Profile[] {
    const start = (this.notCompletedPage - 1) * this.notCompletedPageSize;
    return this.usersNotCompletedFiltered.slice(start, start + this.notCompletedPageSize);
  }

  public get notCompletedTotalPages(): number {
    return Math.ceil(this.usersNotCompletedFiltered.length / this.notCompletedPageSize);
  }

  public notCompletedSearchChanged(): void {
    this.notCompletedPage = 1;
  }

  exportNotCompletedToCsv(): void {
    if (!this.selectedQuiz) return;

    try {
      const headers = ['Username', 'Email'];
      const rows = this.usersNotCompleted.map(user => [`"${user.username || ''}"`, `"${user.meta?.mail || ''}"`]);
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `quiz-not-completed-${this.selectedQuiz.displayName}-${this.dateFrom}-to-${this.dateTo}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      this.alert.fireError('Export Failed', 'Failed to export data to CSV');
    }
  }

  exportNotCompletedToPdf(): void {
    if (!this.selectedQuiz) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Popup blocked');
      }

      const htmlContent = this.generateNotCompletedPdfContent();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      this.alert.fireError('Export Failed', 'Failed to export data to PDF. Please ensure popups are allowed.');
    }
  }

  private generateNotCompletedPdfContent(): string {
    const currentDate = new Date().toLocaleDateString();
    const rows = this.usersNotCompleted.map(user => `<tr><td>${user.username || ''}</td><td>${user.meta?.mail || '—'}</td></tr>`).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Users Not Completed - ${this.selectedQuiz?.displayName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Users Who Have Not Completed the Quiz</h1>
          <h2>${this.selectedQuiz?.displayName}</h2>
          <p>Generated on: ${currentDate}</p>
          <p>Total: ${this.usersNotCompleted.length} users</p>
        </div>
        <table>
          <thead>
            <tr><th>Username</th><th>Email</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
      </html>
    `;
  }

  // Export functions
  exportToCsv(): void {
    if (!this.selectedQuiz) return;

    try {
      const csvContent = this.generateCsvContent();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `quiz-scores-${this.selectedQuiz.displayName}-${this.dateFrom}-to-${this.dateTo}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      this.alert.fireError('Export Failed', 'Failed to export data to CSV');
    }
  }

  exportToPdf(): void {
    if (!this.selectedQuiz) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Popup blocked');
      }

      const htmlContent = this.generatePdfContent();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      this.alert.fireError('Export Failed', 'Failed to export data to PDF. Please ensure popups are allowed.');
    }
  }

  private generateCsvContent(): string {
    const headers = [
      'User',
      'Total Attempts',
      'Passed',
      'Failed',
      'Total Score',
      'Average Score',
      'Total Time (seconds)',
      'Average Time (seconds)',
      'Game ID',
      'Game Score',
      'Questions',
      'Correct Answers',
      'Accuracy %',
      'Game Time (seconds)',
      'Game Status',
      'Game Date'
    ];

    let csvContent = headers.join(',') + '\n';

    Object.keys(this.data).forEach(userId => {
      const userData = this.data[userId];
      const baseUserData = [
        `"${userData.username}"`,
        userData.totals.attempts,
        userData.totals.passed,
        userData.totals.failed,
        userData.totals.score,
        userData.totals.averageScore.toFixed(2),
        userData.totals.totalTime,
        userData.totals.averageTime.toFixed(2)
      ];

      if (userData.games.length === 0) {
        csvContent += baseUserData.join(',') + ',,,,,,,' + '\n';
      } else {
        userData.games.forEach((game, index) => {
          const gameData = [
            game.game_id,
            game.score,
            game.total_questions,
            game.correct_answers,
            this.getAccuracyPercentage(game).toFixed(2),
            game.total_time,
            game.passed === true ? 'Passed' : game.passed === false ? 'Failed' : 'N/A',
            `"${this.formatDateTo24Hour(game.game_started_at)}"`
          ];

          if (index === 0) {
            csvContent += baseUserData.concat(gameData).join(',') + '\n';
          } else {
            const emptyUserData = new Array(8).fill('');
            csvContent += emptyUserData.concat(gameData).join(',') + '\n';
          }
        });
      }
    });

    return csvContent;
  }

  private generatePdfContent(): string {
    const currentDate = new Date().toLocaleDateString();
    const overallStats = {
      totalUsers: this.getTotalUsers(),
      totalAttempts: this.getTotalAttempts(),
      totalPassed: this.getTotalPassed(),
      overallAverage: this.getOverallAverageScore()
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quiz Scores Report - ${this.selectedQuiz?.displayName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .stats-summary {
            background: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;
            display: flex; justify-content: space-around; flex-wrap: wrap;
          }
          .stat-item { text-align: center; margin: 10px; }
          .stat-value { font-size: 24px; font-weight: bold; color: #0066cc; }
          .stat-label { font-size: 12px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .user-header { background-color: #e9ecef; font-weight: bold; }
          .game-row { background-color: #f8f9fa; }
          .text-center { text-align: center; }
          .badge-success { background: #28a745; color: white; padding: 2px 6px; border-radius: 3px; }
          .badge-danger { background: #dc3545; color: white; padding: 2px 6px; border-radius: 3px; }
          .badge-secondary { background: #6c757d; color: white; padding: 2px 6px; border-radius: 3px; }
          h3 { margin-top: 30px; margin-bottom: 15px; color: #495057; border-bottom: 2px solid #dee2e6; padding-bottom: 10px; }
          .page-break { page-break-before: always; }
          @media print {
            body { margin: 0; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Quiz Scores Report</h1>
          <h2>${this.selectedQuiz?.displayName}</h2>
          <p>Generated on: ${currentDate}</p>
          <p><strong>Date Range:</strong> ${new Date(this.dateFrom).toLocaleDateString()} - ${new Date(
      this.dateTo
    ).toLocaleDateString()}</p>
        </div>

        <div class="stats-summary">
          <div class="stat-item">
            <div class="stat-value">${overallStats.totalUsers}</div>
            <div class="stat-label">Total Users</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${overallStats.totalAttempts}</div>
            <div class="stat-label">Total Attempts</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${overallStats.totalPassed}</div>
            <div class="stat-label">Total Passed</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${overallStats.overallAverage.toFixed(1)}</div>
            <div class="stat-label">Overall Avg Score</div>
          </div>
        </div>

        <h3>User Summary</h3>
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Attempts</th>
              <th>Passed</th>
              <th>Failed</th>
              <th>Total Score</th>
              <th>Avg Score</th>
              <th>Total Time</th>
              <th>Avg Time</th>
            </tr>
          </thead>
          <tbody>
            ${Object.keys(this.data)
              .map(userId => {
                const userData = this.data[userId];
                return `
                <tr class="user-header">
                  <td><strong>${userData.username}</strong></td>
                  <td class="text-center">${userData.totals.attempts}</td>
                  <td class="text-center"><span class="badge-success">${userData.totals.passed}</span></td>
                  <td class="text-center"><span class="badge-danger">${userData.totals.failed}</span></td>
                  <td class="text-center">${userData.totals.score}</td>
                  <td class="text-center">${userData.totals.averageScore.toFixed(1)}</td>
                  <td class="text-center">${this.formatTime(userData.totals.totalTime)}</td>
                  <td class="text-center">${this.formatTime(userData.totals.averageTime)}</td>
                </tr>
              `;
              })
              .join('')}
          </tbody>
        </table>

        <div class="page-break"></div>

        <h3>Individual Game Details</h3>
        ${Object.keys(this.data)
          .map((userId, userIndex) => {
            const userData = this.data[userId];
            if (userData.games.length === 0) return '';

            return `
            ${userIndex > 0 ? '<div class="page-break"></div>' : ''}
            <h4 style="margin-top: 20px; color: #212529;">${userData.username}</h4>
            <table style="margin-bottom: 30px;">
              <thead>
                <tr>
                  <th class="text-center">Score</th>
                  <th class="text-center">Questions</th>
                  <th class="text-center">Correct</th>
                  <th class="text-center">Accuracy</th>
                  <th class="text-center">Time</th>
                  <th class="text-center">Status</th>
                  <th class="text-center">Date</th>
                </tr>
              </thead>
              <tbody>
                ${userData.games
                  .map(
                    game => `
                  <tr class="game-row">
                    <td class="text-center"><strong>${game.score}</strong></td>
                    <td class="text-center">${game.total_questions}</td>
                    <td class="text-center">${game.correct_answers}</td>
                    <td class="text-center">${this.getAccuracyPercentage(game).toFixed(0)}%</td>
                    <td class="text-center">${this.formatTime(game.total_time)}</td>
                    <td class="text-center">
                      <span class="${game.passed === true ? 'badge-success' : game.passed === false ? 'badge-danger' : 'badge-secondary'}">
                        ${game.passed === true ? 'Passed' : game.passed === false ? 'Failed' : 'N/A'}
                      </span>
                    </td>
                    <td class="text-center" style="font-size: 10px;">${this.formatDateTo24Hour(game.game_started_at)}</td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          `;
          })
          .join('')}
      </body>
      </html>
    `;
  }

  public async updateReviewMaxAttempts(apply: boolean) {
    if (!apply) {
      this.newReviewMaxAttempts = this.reviewMaxAttempts;
      return;
    }

    try {
      await this.server.updateGameTypeSettings({
        maxAttempts: parseInt(this.newReviewMaxAttempts as any, 10),
        type: 'review'
      });
      this.reviewMaxAttempts = this.newReviewMaxAttempts;
      this.toastr.success('Settings Updated', 'Maximum attempts updated successfully.');
    } catch (error: any) {
      this.alert.fireError('Update Failed', error.message || 'Failed to update settings');
    }
  }
}
